import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the desktop drawer menu.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [drawer] - The drawer elements
 * @property {HTMLElement[]} [level3Panel] - The level 3 panels
 * @property {HTMLElement} [level3Container] - The level 3 container
 *
 * @extends {Component<Refs>}
 */
class HeaderDesktopDrawer extends Component {
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #activeDrawer = null;

  /** @type {HTMLElement | null} */
  #activeLevel3Panel = null;

  /** @type {HTMLElement | null} */
  #activeLevel2Item = null;

  /** @type {HTMLElement | null} */
  #activeTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('keydown', this.#onKeyDown, {
      signal: this.#abortController.signal,
    });

    document.addEventListener('click', this.#onDocumentClick, {
      signal: this.#abortController.signal,
    });

    // Set up click listeners for drawer triggers
    this.#setupTriggerListeners();

    // Set up event listeners for drawer elements
    this.#setupDrawerListeners();
  }

  /**
   * Set up click listeners for drawer trigger buttons
   */
  #setupTriggerListeners() {
    const triggers = this.querySelectorAll('[data-desktop-drawer-trigger]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#onTriggerClick, {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Set up event listeners for drawer elements
   */
  #setupDrawerListeners() {
    // Backdrop click to close
    const backdrops = this.querySelectorAll('.desktop-drawer__backdrop');
    backdrops.forEach((backdrop) => {
      backdrop.addEventListener('click', () => this.closeDrawer(), {
        signal: this.#abortController.signal,
      });
    });

    // Level 2 hover to show level 3
    const level2Items = this.querySelectorAll('.desktop-drawer__level2-item');
    level2Items.forEach((item) => {
      item.addEventListener('pointerenter', (event) => this.showLevel3(event), {
        signal: this.#abortController.signal,
      });
      item.addEventListener('focusin', (event) => this.showLevel3(event), {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Handle click on drawer trigger
   * @param {MouseEvent} event
   */
  #onTriggerClick = (event) => {
    event.preventDefault();
    this.toggleDrawer(event);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handle keydown events
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#activeDrawer) {
      this.closeDrawer();
    }
  };

  /**
   * Handle clicks outside the drawer to close it
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#activeDrawer) return;
    if (!(event.target instanceof Element)) return;

    // Check if click is inside the drawer or the trigger
    const isInsideDrawer = event.target.closest('.desktop-drawer');
    const isOnTrigger = event.target.closest('[data-desktop-drawer-trigger]');

    if (!isInsideDrawer && !isOnTrigger) {
      this.closeDrawer();
    }
  };

  /**
   * Toggle a drawer based on the clicked trigger
   * @param {Event} event
   */
  toggleDrawer(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-drawer-index]');
    if (!trigger) return;

    const index = trigger.dataset.drawerIndex;
    const drawer = this.querySelector(`#desktop-drawer-${index}`);

    if (!drawer) return;

    if (this.#activeDrawer === drawer) {
      this.closeDrawer();
    } else {
      this.openDrawer(drawer, trigger);
    }
  }

  /**
   * Open a specific drawer
   * @param {HTMLElement} drawer
   * @param {HTMLElement | null} [trigger]
   */
  openDrawer(drawer, trigger = null) {
    // Close any open drawer first
    if (this.#activeDrawer && this.#activeDrawer !== drawer) {
      this.#closeDrawerElement(this.#activeDrawer);
    }

    // Update trigger aria-expanded
    if (this.#activeTrigger && this.#activeTrigger !== trigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
    }
    if (trigger) {
      this.#activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.#activeDrawer = drawer;
    drawer.setAttribute('aria-hidden', 'false');

    // Set up focus trap
    requestAnimationFrame(() => {
      trapFocus(drawer);
    });

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('desktop-drawer:open', {
      bubbles: true,
      detail: { drawer },
    }));
  }

  /**
   * Close the currently open drawer
   */
  closeDrawer() {
    if (!this.#activeDrawer) return;

    this.#closeDrawerElement(this.#activeDrawer);
    this.#activeDrawer = null;
    this.#hideLevel3Panel();
    removeTrapFocus();

    // Update trigger aria-expanded
    if (this.#activeTrigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
      this.#activeTrigger = null;
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('desktop-drawer:close', {
      bubbles: true,
    }));
  }

  /**
   * Close a specific drawer element
   * @param {HTMLElement} drawer
   */
  #closeDrawerElement(drawer) {
    drawer.setAttribute('aria-hidden', 'true');

    // Clear active level 2 item
    if (this.#activeLevel2Item) {
      this.#activeLevel2Item.removeAttribute('data-active');
      this.#activeLevel2Item = null;
    }
  }

  /**
   * Show the level 3 panel for a level 2 item
   * @param {Event} event
   */
  showLevel3(event) {
    if (!(event.target instanceof Element)) return;

    const level2Item = event.target.closest('.desktop-drawer__level2-item');
    if (!level2Item) return;

    const level3Id = level2Item.dataset.level3Id;
    const hasChildren = level2Item.dataset.hasChildren !== '0';

    // Update active level 2 item styling
    if (this.#activeLevel2Item && this.#activeLevel2Item !== level2Item) {
      this.#activeLevel2Item.removeAttribute('data-active');
    }
    this.#activeLevel2Item = level2Item;
    level2Item.setAttribute('data-active', 'true');

    // Show level 3 panel if this item has children
    if (hasChildren && level3Id) {
      const level3Panel = document.getElementById(level3Id);
      if (level3Panel) {
        this.#showLevel3Panel(level3Panel);
      }
    } else {
      this.#hideLevel3Panel();
    }
  }

  /**
   * Show a specific level 3 panel
   * @param {HTMLElement} panel
   */
  #showLevel3Panel(panel) {
    // Hide current panel if different
    if (this.#activeLevel3Panel && this.#activeLevel3Panel !== panel) {
      this.#activeLevel3Panel.setAttribute('aria-hidden', 'true');
    }

    this.#activeLevel3Panel = panel;
    panel.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide the currently visible level 3 panel
   */
  #hideLevel3Panel() {
    if (this.#activeLevel3Panel) {
      this.#activeLevel3Panel.setAttribute('aria-hidden', 'true');
      this.#activeLevel3Panel = null;
    }
  }

  /**
   * Check if any drawer is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.#activeDrawer !== null;
  }

  /**
   * Get the currently active drawer
   * @returns {HTMLElement | null}
   */
  get activeDrawer() {
    return this.#activeDrawer;
  }
}

if (!customElements.get('header-desktop-drawer')) {
  customElements.define('header-desktop-drawer', HeaderDesktopDrawer);
}
